import { CheckCircle, AlertCircle } from 'lucide-react'

interface CSVError {
  row: number
  message: string
  data?: any
}

interface CSVWarning {
  row: number
  message: string
  data?: any
}

interface CSVValidationSummaryProps {
  success: boolean
  totalRows: number
  processedRows: number
  skipped: number
  errors: CSVError[]
  warnings: CSVWarning[]
  entityType: 'contacts' | 'properties' | 'units'
}

export function CSVValidationSummary({
  success,
  totalRows: _totalRows,
  processedRows,
  skipped,
  errors,
  warnings,
  entityType,
}: CSVValidationSummaryProps) {
  return (
    <div className="space-y-4">
      {/* Status Header */}
      <div className="flex items-center space-x-2">
        {success ? (
          <CheckCircle className="h-5 w-5 text-success-dim" />
        ) : (
          <AlertCircle className="h-5 w-5 text-error-dim" />
        )}
        <span className="font-medium text-sm">
          {success
            ? skipped > 0
              ? `Found ${processedRows} valid ${entityType} (${skipped} skipped)`
              : `Found ${processedRows} valid ${entityType}`
            : `${errors.length} critical errors found`}
        </span>
      </div>

      {/* Warnings Section */}
      {warnings.length > 0 && (
        <div>
          <div className="max-h-32 overflow-y-auto space-y-1">
            {warnings.slice(0, 5).map((warning, index) => (
              <div
                key={index}
                className="text-xs text-warning bg-warning-subtle p-2 rounded"
              >
                Row {warning.row}: {warning.message}
              </div>
            ))}
            {warnings.length > 5 && (
              <div className="text-xs text-muted-foreground">
                ... and {warnings.length - 5} more warnings
              </div>
            )}
          </div>
        </div>
      )}

      {/* Errors Section */}
      {errors.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-2 text-error">
            Critical Errors:
          </h4>
          <div className="max-h-32 overflow-y-auto space-y-1">
            {errors.slice(0, 5).map((error, index) => (
              <div
                key={index}
                className="text-xs text-error bg-error-subtle p-2 rounded"
              >
                Row {error.row}: {error.message}
              </div>
            ))}
            {errors.length > 5 && (
              <div className="text-xs text-muted-foreground">
                ... and {errors.length - 5} more critical errors
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
